import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Form,
    FormField,
    Heading,
    Layer,
    Text,
    TextInput,
    Tip,
    ToggleGroup,
} from 'grommet';
import { Close, FormAdd } from 'grommet-icons';
import PropTypes from 'prop-types';
import QueryString from 'qs';
import { API, FilterOptionSelect, PipelineService } from '@enrich/shared-lib';

export const AddCompanyPipelineButton = ({ companyId } = {}) => {
    const [showModal, setShowModal] = useState(false);

    return (
        <>
            <Tip content="new pipeline">
                <Button
                    secondary
                    size="small"
                    label=<span>
                        <FormAdd size="medium" color="enrich-blue" />
                    </span>
                    onClick={() => setShowModal(true)}
                />
            </Tip>
            {showModal && (
                <AddCompanyPipelineModal
                    companyId={companyId}
                    setShowModal={setShowModal}
                />
            )}
        </>
    );
};

const AddCompanyPipelineModal = ({ companyId, setShowModal }) => {
    const [companyJobs, setCompanyJobs] = useState([]);

    useEffect(() => {
        let isActive = true;
        (async () => {
            const loadedJobs = await loadCompanyJobs(companyId);

            if (isActive)
                setCompanyJobs(
                    loadedJobs.data
                        .map((job) => ({
                            ...job.attributes,
                            id: job.id,
                        }))
                        .sort((a, b) => b.title < a.title),
                );
        })();

        return () => (isActive = false);
    }, []);

    const defaultValues = {
        name: '',
        company: companyId,
        jobs: '',
        isReportingActive: false,
    };

    const [value, setValue] = useState(defaultValues);
    const [buttonBusy, setButtonBusy] = useState();

    const handleSubmit = async () => {
        try {
            setButtonBusy(true);
            await new PipelineService().create(value);
            setShowModal(false);
            location.reload();
        } catch (error) {
            console.log(error);
        }

        setButtonBusy(false);
    };

    return (
        <Layer
            onClickOutside={() => setShowModal(false)}
            onEsc={() => setShowModal(false)}
            position="center">
            <Box
                pad="medium"
                round="6px"
                border={{ color: 'enrich-green-light', size: '3px' }}
                gap="30px"
                width="large">
                <Box fill={'horizontal'} direction="row" justify="between">
                    <Box>
                        <Heading level="3" margin={{ vertical: 'none' }}>
                            New Pipeline
                        </Heading>
                    </Box>
                    <Box
                        align="end"
                        round={true}
                        onClick={() => {
                            setShowModal(false);
                        }}
                        cursor="pointer">
                        <Close color="enrich-red" />
                    </Box>
                </Box>
                <Box fill="horizontal">
                    <Form
                        value={value}
                        onChange={(nextValue) => setValue(nextValue)}
                        onReset={() => setValue(defaultValues)}
                        onSubmit={({ value }) => {
                            handleSubmit(value);
                        }}>
                        <Box gap="small">
                            <Box
                                direction="row-responsive"
                                gap="medium"
                                fill="horizontal">
                                <Box basis="large">
                                    <FormField
                                        label="Name"
                                        name="name"
                                        htmlFor="name"
                                        required>
                                        <TextInput
                                            id="name"
                                            name="name"
                                            required
                                        />
                                    </FormField>
                                </Box>
                                <Box basis="medium">
                                    <Tip
                                        content={
                                            value.isReportingActive
                                                ? 'reporting activated'
                                                : 'reporting deactivated'
                                        }>
                                        <FormField
                                            label="Reporting Status"
                                            name="isReportingActive">
                                            <ToggleGroup
                                                a11yTitle="Toggle Reporting active status for this school"
                                                margin={{ left: 'small' }}
                                                // eslint-disable-next-line max-len
                                                value={value.isReportingActive.toString()}
                                                onToggle={({
                                                    value: toggleValue,
                                                }) =>
                                                    setValue({
                                                        ...value,
                                                        isReportingActive:
                                                            toggleValue ===
                                                            'true',
                                                    })
                                                }
                                                options={[
                                                    {
                                                        label: (
                                                            <Text color="enrich-red">
                                                                OFF
                                                            </Text>
                                                        ),
                                                        value: 'false',
                                                    },
                                                    {
                                                        label: (
                                                            <Text color="enrich-green">
                                                                ON
                                                            </Text>
                                                        ),
                                                        value: 'true',
                                                    },
                                                ]}
                                            />
                                        </FormField>
                                    </Tip>
                                </Box>
                            </Box>

                            <Box>
                                <FormField
                                    name="jobs"
                                    htmlFor="jobs"
                                    label="Jobs">
                                    <FilterOptionSelect
                                        labelKey="title"
                                        placeholder="select jobs to add to pipeline"
                                        options={companyJobs}
                                        updateFormData={(selectedJobs) => {
                                            setValue({
                                                ...value,
                                                jobs: selectedJobs.map(
                                                    (x) => x.id,
                                                ),
                                            });
                                        }}
                                        valueKey="id"
                                    />
                                </FormField>
                            </Box>

                            <Box margin={{ top: 'large' }}>
                                <Button
                                    primary
                                    type="submit"
                                    size="large"
                                    fill="horizontal"
                                    busy={buttonBusy}
                                    label="Submit"
                                    alignSelf="center"
                                />
                            </Box>
                        </Box>
                    </Form>
                </Box>
            </Box>
        </Layer>
    );
};

const loadCompanyJobs = async (companyId) => {
    const query = QueryString.stringify(
        {
            filters: {
                company: { id: { $eq: companyId } },
                pipeline: { id: { $null: true } },
            },
            fields: ['id', 'title'],
            sort: ['title:asc'],
            pagination: { limit: 10000 },
        },
        { encodeValuesOnly: true },
    );

    try {
        const response = await API.get(`/api/jobs?${query}`);
        return response.data;
    } catch (error) {
        console.log(error);
    }

    return [];
};

AddCompanyPipelineButton.propTypes = {
    companyId: PropTypes.string,
};

AddCompanyPipelineModal.propTypes = {
    companyId: PropTypes.string,
    setShowModal: PropTypes.func,
};
